<template>
  <div
    class="drag-drop-input"
    :class="{
      'drag-drop-input--dragover': isDragging,
      'drag-drop-input--error': hasError,
    }"
    @dragenter.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @dragover.prevent
    @drop.prevent="onDrop"
    data-test-id="drag-and-drop-file-input"
  >
    <input
      ref="fileInput"
      type="file"
      :accept="accept"
      :aria-label="title"
      class="hidden-input"
      @change="onFileSelect"
    />
    <template v-if="!file && !existingImageUrl">
      <div class="icon-tile" :class="hasError ? 'icon-tile--error' : 'icon-tile--primary'">
        <v-icon size="24" :color="hasError ? 'error' : 'white'">{{
          hasError ? "mdi-alert-circle" : "mdi-upload"
        }}</v-icon>
      </div>
      <Typography variant="h-4" :class="{ 'error--text': hasError }">{{ displayTitle }}</Typography>
      <Typography variant="body-1">{{ hasError ? "Drop a new file or" : "or" }}</Typography>
      <Button class="align-self-center" :loading="loading" @click="$refs.fileInput.click()">
        {{ buttonText }}
      </Button>
    </template>
    <div v-else class="selected-file" :class="{ 'selected-file--image': previewImage }">
      <div class="d-flex align-center gap-4 file-info-row">
        <img
          v-if="previewImage && displayPreviewUrl"
          :src="displayPreviewUrl"
          class="image-preview"
          alt="Uploaded image preview"
        />
        <div v-else class="icon-tile">
          <v-icon size="24" color="white">mdi-file</v-icon>
        </div>
        <div class="text-left wrap-text">
          <Typography v-if="file" variant="body-2">{{ file.name }}</Typography>
          <Typography v-if="file" variant="caption-2" class="file-size">{{
            formatFileSize(file.size)
          }}</Typography>
          <Typography v-else-if="existingImageUrl" variant="body-2">{{
            existingFileName
          }}</Typography>
        </div>
      </div>
      <Button
        text
        class="align-self-center"
        color="mediumGrey"
        @click="handleRemove"
        aria-label="Remove file"
      >
        <v-icon size="24">mdi-close</v-icon>
      </Button>
    </div>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import fileMixin from "@/mixins/fileMixin";

export default {
  name: "DragAndDropFileInput",
  components: {
    Typography,
    Button,
  },
  mixins: [fileMixin],
  props: {
    accept: {
      type: String,
      default: "*",
    },
    loading: {
      type: Boolean,
      default: false,
    },
    file: {
      type: File,
      default: null,
    },
    title: {
      type: String,
      default: "Drag and drop your file here",
    },
    buttonText: {
      type: String,
      default: "Browse Files",
    },
    previewImage: {
      type: Boolean,
      default: false,
    },
    existingImageUrl: {
      type: String,
      default: null,
    },
    maxFileSize: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      isDragging: false,
      error: null,
      imagePreviewUrl: null,
    };
  },
  watch: {
    file: {
      immediate: true,
      handler(newFile) {
        if (this.imagePreviewUrl) {
          URL.revokeObjectURL(this.imagePreviewUrl);
          this.imagePreviewUrl = null;
        }
        if (this.previewImage && newFile) {
          this.imagePreviewUrl = URL.createObjectURL(newFile);
        }
      },
    },
  },
  beforeUnmount() {
    if (this.imagePreviewUrl) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }
  },
  computed: {
    displayPreviewUrl() {
      return this.imagePreviewUrl || this.existingImageUrl;
    },
    existingFileName() {
      if (!this.existingImageUrl) return "";
      try {
        const pathname = new URL(this.existingImageUrl).pathname;
        return decodeURIComponent(pathname.split("/").pop()) || "Image";
      } catch {
        return this.existingImageUrl.split("/").pop() || "Image";
      }
    },
    hasError() {
      return this.error !== null;
    },
    displayTitle() {
      if (this.error === "size") {
        const maxMB = (this.maxFileSize / 1024 / 1024).toFixed(0);
        return `Maximum file size is ${maxMB} MB`;
      }
      if (this.error === "type") {
        const extensions = this.accept
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.startsWith("."))
          .join(", ");
        return `Invalid file type. File must be ${extensions}`;
      }
      return this.title;
    },
  },
  methods: {
    onDragEnter() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;

      const droppedFiles = event.dataTransfer.files;
      if (droppedFiles.length > 0) {
        const droppedFile = droppedFiles[0];
        this.handleFile(droppedFile);
      }
    },
    onFileSelect(event) {
      const selectedFile = event.target.files[0];
      if (selectedFile) {
        this.handleFile(selectedFile);
      }
      this.$refs.fileInput.value = "";
    },
    handleFile(file) {
      this.error = null;

      if (!this.isValidFileType(file, this.accept)) {
        this.error = "type";
        this.clearFileInput();
        this.$emit("file-rejected", { reason: "type", file });
        return;
      }

      if (!this.isValidFileSize(file, this.maxFileSize)) {
        this.error = "size";
        this.clearFileInput();
        this.$emit("file-rejected", { reason: "size", file });
        return;
      }

      this.$emit("file-selected", file);
    },
    clearFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
        this.$emit("file-selected", null);
      }
    },
    handleRemove() {
      this.$emit("file-selected", null);
      this.$emit("clear-existing-image");
    },
    // Public: re-run the size check on a file that bypassed handleFile()
    // (e.g. a post-crop output produced elsewhere). Mirrors the size
    // branch of handleFile — sets internal error state so the drop zone
    // renders the same "Maximum file size is X MB" message. Returns
    // true when the file is within the limit.
    validateFileSize(file) {
      this.error = null;
      if (!this.isValidFileSize(file, this.maxFileSize)) {
        this.error = "size";
        return false;
      }
      return true;
    },
  },
};
</script>

<style scoped>
.drag-drop-input {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 2px dashed rgba(0, 0, 0, 0.2);
  padding: 48px 24px;
  gap: 4px;
  transition: border-color 0.2s, background-color 0.2s;
  position: relative;
}

.drag-drop-input--dragover {
  border-color: var(--v-brandPrimary-base);
  background-color: rgba(var(--v-brandPrimary-base), 0.05);
}

.drag-drop-input--error {
  border-color: var(--v-error-base);
}

.hidden-input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  overflow: hidden;
}

.selected-file {
  display: flex;
  flex-direction: row;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.5rem;
  padding: 8px 12px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
}
.icon-tile {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 4px;
  background: var(--Action-Focus, rgba(0, 0, 0, 0.2));
  padding: 8px;
}

.icon-tile--primary {
  background: var(--v-brandPrimary-base);
}

.icon-tile--error {
  background: #ffebee;
}

.close-button {
  align-self: center;
}

.file-size {
  color: var(--Text-On-Light-Medium-Emphasis, rgba(0, 0, 0, 0.6));
}

.selected-file--image {
  border-style: solid;
}

.image-preview {
  width: 182px;
  height: 124px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.gap-4 {
  gap: 16px;
}
.file-info-row {
  flex: 1;
  min-width: 0;
}

.wrap-text {
  min-width: 0;
  overflow-wrap: break-word;
  word-break: break-word;
  text-align: left;
}
</style>
